import { DropDownFieldDefinition } from "@insite/client-framework/Types/FieldDefinition";
import { BreakpointValue } from "@store/Framework/Common/Utilities/breakpointValues";
import {
    DataOption,
    DataOptions,
    getDataOptionsFieldDefinition,
    getDataOptionThemeValue,
} from "@store/Framework/Common/Utilities/dataOptions";
import { LiteralUnion } from "@store/Framework/Types/Common";
import { StyledTheme } from "@store/Framework/Types/StyledTheme";
import { useMemo } from "react";
import { useTheme } from "styled-components";

export type SpacingPresetValue = BreakpointValue[];
export type SpacingPresetOption = DataOption<SpacingPresetValue | string>;
export interface SpacingPresetOptionMapper {
    standard: SpacingPresetOption;
    narrow: SpacingPresetOption;
    content: SpacingPresetOption;
}
export type SpacingPresetOptions = DataOptions<SpacingPresetOption> & SpacingPresetOptionMapper;
export type SpacingPreset = LiteralUnion<keyof SpacingPresetOptionMapper>;

export const spacingPresetOptions: SpacingPresetOptions = {
    standard: { id: "standard", displayName: "Standard", value: "design.layout.spacing.content" },
    narrow: { id: "narrow", displayName: "Narrow", value: "design.layout.spacing.narrowContent" },
    content: { id: "content", displayName: "Content", value: "design.typography.spacing.base" },
};

export type SpacingPresetFieldValue = SpacingPreset | undefined;
export interface SpacingPresetFieldOptions {
    isChildDefinition?: boolean;
}

export const getSpacingPresetFieldData = (
    value: SpacingPresetFieldValue,
    theme: StyledTheme,
    options?: SpacingPresetFieldOptions,
) => {
    if (!value) {
        return undefined;
    }

    const data = getDataOptionThemeValue<SpacingPresetValue>(spacingPresetOptions, value, theme);

    return data ? (Array.isArray(data) ? data : [data]) : undefined;
};

export const getSpacingPresetFieldDefinition = (name: string, options?: SpacingPresetFieldOptions) => {
    const { isChildDefinition } = options || {};

    const definition: DropDownFieldDefinition<string> = {
        ...getDataOptionsFieldDefinition(spacingPresetOptions),
        name,
        displayName: "Spacing Preset",
        fieldType: !isChildDefinition ? "General" : (undefined as any),
    };

    return definition;
};

const getSpacingPresetField = (name: string, options?: SpacingPresetFieldOptions) => {
    const parse = (value: SpacingPresetFieldValue, theme: StyledTheme) => {
        return getSpacingPresetFieldData(value, theme, options);
    };

    return {
        definition: getSpacingPresetFieldDefinition(name, options),
        parse,
        useParse: (value: SpacingPresetFieldValue) => {
            const theme = useTheme();

            return useMemo(() => parse(value, theme), [value, theme]);
        },
    };
};

export default getSpacingPresetField;
